import type { SheetsClient } from '../client';
import type { RecurringRow } from '../types';
import {
  appendRow,
  genericSoftDelete,
  genericUpdate,
  newRowDefaults,
  readAllRows,
  type ListOptions,
} from './_base';
import { parseBool, parseInt10 } from '../row-utils';

const TAB = 'recurring' as const;

function parse(o: Record<string, string>): RecurringRow {
  return {
    id: o['id'] ?? '',
    book_id: o['book_id'] ?? '',
    title: o['title'] ?? '',
    amount_minor: parseInt10(o['amount_minor'], 0),
    currency: o['currency'] ?? 'TWD',
    kind: ((o['kind'] ?? 'expense') as RecurringRow['kind']),
    category_id: o['category_id'] ?? '',
    subcategory_id: o['subcategory_id'] ?? '',
    note: o['note'] ?? '',
    freq: ((o['freq'] ?? 'monthly') as RecurringRow['freq']),
    freq_day: parseInt10(o['freq_day'], 1),
    start_date: o['start_date'] ?? '',
    end_date: (o['end_date'] ?? '') as RecurringRow['end_date'],
    last_generated_date: (o['last_generated_date'] ?? '') as RecurringRow['last_generated_date'],
    active: parseBool(o['active']),
    rev: parseInt10(o['rev'], 0),
    created_at: o['created_at'] ?? '',
    updated_at: o['updated_at'] ?? '',
    deleted_at: (o['deleted_at'] ?? '') as RecurringRow['deleted_at'],
  };
}

export const recurringRepo = {
  async list(
    client: SheetsClient,
    spreadsheetId: string,
    options?: ListOptions,
  ): Promise<RecurringRow[]> {
    const rows = await readAllRows(client, spreadsheetId, TAB, options);
    const out: RecurringRow[] = [];
    for (const r of rows) {
      try {
        out.push(parse(r));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Skipping malformed recurring row', { id: r['id'], err });
      }
    }
    return out;
  },

  async get(
    client: SheetsClient,
    spreadsheetId: string,
    id: string,
  ): Promise<RecurringRow | null> {
    const all = await readAllRows(client, spreadsheetId, TAB, { includeDeleted: true });
    const row = all.find((r) => r['id'] === id);
    return row ? parse(row) : null;
  },

  async create(
    client: SheetsClient,
    spreadsheetId: string,
    input: Partial<RecurringRow> & { id?: string },
  ): Promise<RecurringRow> {
    const row = newRowDefaults({
      id: input.id,
      book_id: input.book_id ?? '',
      title: input.title ?? '',
      amount_minor: input.amount_minor ?? 0,
      currency: input.currency ?? 'TWD',
      kind: input.kind ?? 'expense',
      category_id: input.category_id ?? '',
      subcategory_id: input.subcategory_id ?? '',
      note: input.note ?? '',
      freq: input.freq ?? 'monthly',
      freq_day: input.freq_day ?? 1,
      start_date: input.start_date ?? '',
      end_date: input.end_date ?? '',
      last_generated_date: input.last_generated_date ?? '',
      active: input.active ?? true,
    });
    await appendRow(client, spreadsheetId, TAB, row);
    return parse(row);
  },

  update(
    client: SheetsClient,
    spreadsheetId: string,
    id: string,
    patch: Partial<RecurringRow>,
    expectedRev: number,
  ) {
    return genericUpdate(client, spreadsheetId, TAB, id, patch as Record<string, unknown>, expectedRev);
  },

  softDelete(
    client: SheetsClient,
    spreadsheetId: string,
    id: string,
    expectedRev: number,
  ) {
    return genericSoftDelete(client, spreadsheetId, TAB, id, expectedRev);
  },
};
